import { useState } from "react";
import { Pencil, ShieldCheck, Trash2, Plus, Users } from "lucide-react";
import { api } from "../../lib/api";
import type { DashboardData, Owner, SplitType } from "../../types";
import { AVATAR_KEYS, LEGACY_PROFILES, PROFILE_NAME_MAX, checkProfileName, type AvatarKey, type ProfileInfo } from "../../lib/profiles";
import Avatar from "../../components/Avatar";
import ConfirmDialog, { type ConfirmState } from "../../components/ConfirmDialog";
import Modal from "../../components/Modal";
import FormActions from "../../components/FormActions";
import SecondaryHeading from "../../app/SecondaryHeading";
import AvatarPicker from "./AvatarPicker";
import { useSplitPreferences } from "../../hooks/use-split-preferences";

type Draft = { id?: string; name: string; avatar: AvatarKey; nameLocked: boolean };
const request = (path: string, body: unknown) => api<{ name: string }>(`settings/profiles/${path}`, body);

export default function ProfileSettings({ data, profile, onSaved, onSwitchProfile }: {
  data: DashboardData;
  profile: Owner;
  onSaved: () => Promise<unknown>;
  onSwitchProfile: (name: Owner) => Promise<unknown>;
}) {
  const { defaultSplitType, setDefaultSplitType } = useSplitPreferences();
  const [draft, setDraft] = useState<Draft | null>(null), [busy, setBusy] = useState(false), [error, setError] = useState(""), [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const profiles: ProfileInfo[] = data.profiles ?? LEGACY_PROFILES;
  const ownerName = profiles.find((p) => p.owner)?.name ?? "Bun";
  const nextAvatar = () => AVATAR_KEYS.find((k) => !profiles.some((p) => p.avatar === k)) ?? AVATAR_KEYS[0]!;

  async function save() {
    if (!draft || busy) return;
    const checked = checkProfileName(draft.name);
    if (checked.error !== undefined) { setError(checked.error); return; }
    setBusy(true); setError("");
    try {
      const saved = await request("save", { id: draft.id, name: checked.name, avatar: draft.avatar });
      // The sign-in cookie names the profile, so a profile renamed from under itself needs a new one.
      if (draft.id && saved.name !== profile && profiles.find((p) => p.id === draft.id)?.name === profile) await onSwitchProfile(saved.name);
      setDraft(null); await onSaved();
    }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }
  async function remove(p: ProfileInfo) {
    setBusy(true); setError("");
    try {
      await request("remove", { id: p.id });
      if (p.name === profile) await onSwitchProfile(ownerName);
      setDraft(null); await onSaved();
    }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  return <section className="panel account-settings profile-settings">
    <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    <SecondaryHeading title="Profiles" icon={<Users size={18} />}><button className="primary small" onClick={() => { setDraft({ name: "", avatar: nextAvatar(), nameLocked: false }); setError(""); }}><Plus size={14}/> Add</button></SecondaryHeading>
    {error && !draft && <div className="inline-error" role="alert">{error}</div>}
    {draft && (
      <Modal
        title={draft.id ? "Edit profile" : "New profile"}
        onClose={() => {
          setDraft(null);
          setError("");
        }}
      >
        <form
          className="account-editor"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          {error && <div className="inline-error" role="alert">{error}</div>}
          <label>
            Name
            <input
              required
              autoFocus={!draft.id}
              value={draft.name}
              disabled={draft.nameLocked}
              maxLength={PROFILE_NAME_MAX}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="e.g. Grandma"
            />
          </label>
          {draft.nameLocked && (
            <p className="settings-note">
              {draft.name}’s name is tied to their passcode, so only the picture can change.
            </p>
          )}
          <div className="avatar-picker-field">
            <span>Picture</span>
            <AvatarPicker
              value={draft.avatar}
              onChange={(avatar) => setDraft({ ...draft, avatar })}
              name={draft.name}
            />
          </div>
          <FormActions
            label="Save profile"
            busy={busy}
            disabled={!draft.name.trim()}
          />
        </form>
      </Modal>
    )}
    <div className="managed-account-list">{profiles.filter((p) => p.id).map((p) => <article key={p.id} className="managed-account">
      <Avatar owner={p.name} size={30} />
      <span><strong>{p.name}</strong><small>{p.owner ? "Owner · can’t be deleted" : p.nameLocked ? "Signs in with a passcode" : "No passcode · open from the profile menu"}</small></span>
      <div>
        <button aria-label={`Edit ${p.name}`} onClick={() => { setDraft({ id: p.id, name: p.name, avatar: p.avatar, nameLocked: p.nameLocked }); setError(""); }}><Pencil size={17}/></button>
        {p.owner
          ? <span className="managed-account-lock" title="The owner profile can’t be deleted"><ShieldCheck size={16}/></span>
          : <button aria-label={`Delete ${p.name}`} disabled={busy} onClick={() => setConfirm({ title: `Delete ${p.name}?`, message: "They disappear from pickers and any budget set for them is removed. Transactions they added keep their name. A profile with active accounts can’t be deleted.", confirmLabel: "Delete profile", danger: true, onConfirm: () => remove(p) })}><Trash2 size={17}/></button>}
      </div>
    </article>)}</div>
    <div className="section-heading" style={{ marginTop: 28 }}>
      <div>
        <h2>Sharing & default splits</h2>
        <p className="muted" style={{ margin: 0, fontSize: "0.85rem" }}>Choose how new expenses are split between household members by default.</p>
      </div>
    </div>
    <div className="split-defaults-selector" style={{ marginTop: 12 }}>
      <label>
        Default split rule
        <select
          value={defaultSplitType}
          onChange={(e) => setDefaultSplitType(e.target.value as SplitType)}
        >
          <option value="joint">Joint (Shared) — Nobody owes anything</option>
          <option value="equal">Equal (50/50) — Split evenly among profiles</option>
          <option value="personal">Personal — 100% personal to the payer</option>
        </select>
      </label>
      <small className="muted" style={{ display: "block", marginTop: 8, fontSize: "0.8125rem", lineHeight: 1.5 }}>
        {defaultSplitType === "joint" && "New expenses are shared by the whole household. No debt is tracked between profiles."}
        {defaultSplitType === "equal" && "New expenses are divided evenly between members. Non-payers will owe their share to the payer."}
        {defaultSplitType === "personal" && "New expenses are marked as personal to whoever paid them."}
      </small>
    </div>
  </section>;
}
